import { ref, type Ref } from 'vue';
import type { ObjectStore } from './object-store';

export type AppLayoutCode = 'tabbed' | 'sidebar';

export interface AppLayoutOption {
  code: AppLayoutCode;
  label: string;
}

export const AVAILABLE_LAYOUTS: ReadonlyArray<AppLayoutOption> = [
  { code: 'tabbed', label: 'Tabbed' },
  { code: 'sidebar', label: 'Sidebar' },
];

export const DEFAULT_APP_LAYOUT: AppLayoutCode = 'tabbed';

export const APP_LAYOUT_STORAGE_KEY = 'app.layout';

export function isAppLayoutCode(v: unknown): v is AppLayoutCode {
  return typeof v === 'string' && AVAILABLE_LAYOUTS.some((l) => l.code === v);
}

export class AppLayoutManager {
  private readonly _ref: Ref<AppLayoutCode>;
  private readonly objectStore: ObjectStore | null;

  constructor(objectStore?: ObjectStore | null) {
    this.objectStore = objectStore ?? null;
    this._ref = ref(DEFAULT_APP_LAYOUT) as Ref<AppLayoutCode>;
    this.hydrateFromStore();
  }

  get value(): AppLayoutCode {
    return this._ref.value;
  }

  set value(next: AppLayoutCode) {
    const validated: AppLayoutCode = isAppLayoutCode(next)
      ? next
      : DEFAULT_APP_LAYOUT;
    if (this._ref.value === validated) {
      return;
    }
    this._ref.value = validated;
    if (this.objectStore) {
      this.objectStore.setItem(APP_LAYOUT_STORAGE_KEY, { value: validated });
    }
  }

  asRef(): Ref<AppLayoutCode> {
    return this._ref;
  }

  private hydrateFromStore(): void {
    if (!this.objectStore) {
      return;
    }
    const stored = this.objectStore.getItem(APP_LAYOUT_STORAGE_KEY, null);
    if (stored && typeof stored === 'object') {
      const raw = (stored as { value?: unknown }).value;
      if (isAppLayoutCode(raw)) {
        this._ref.value = raw;
      }
    }
  }
}
